import React from 'react';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { googleLoginOneTap } from '@eb/social-login-facebook';

import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eb/social-login-facebook';

import { AlertChunky } from '@eb/eds-iconography';

import { parseFormPostData } from '../api/utils';
import { socialLogIn as socialLogInApi } from '../api';

import {
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
} from '../constants';
import {
    ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
    SOCIAL_ERROR_POPUP_BLOCKED,
    SOCIAL_ERROR_UNKNOWN,
    SOCIAL_ERROR_NOT_AUTHORIZED,
    SOCIAL_ERROR_SERVER,
} from '../constants/messages';
import {
    GA_AUTH_CHECKOUT_CATEGORY,
    TOSMODAL_SOCIAL_SHOWED,
    GA_GOOGLE,
} from '../constants/analytics';

import {
    trackFBLoginSuccess,
    trackSocialSignupEvent,
    trackAccountAutolinked,
    trackLoginSuccessful,
    trackGoogleLoginSuccess,
    trackSocialPermissionNotGranted,
    trackSocialPermissionGranted,
    trackGoogleOneTapTriggered,
    trackGoogleOneTapClicked,
    trackGoogleOneTapNotDisplayed,
    trackGoogleOneTapBlocked,
} from './gaTracking';

import { notifyParent } from '../utils';

import {
    getSocialLogInFormData,
    getSocialSignUpFormData,
    shouldOnboardingRedirect,
} from './utils';

import {
    submitSuccessRedirect,
    handleOnError,
    showAuthNotification,
    clearAuthNotification,
    trackSocialProviderClick,
} from './auth';
import { lookupUser } from './lookup';
import { setTosSocialAccepted } from './tos';

import { handleAppleLoginAttempt } from './appleLogin';

import { onSkipSocialLinks } from './mocks';
import { showToS } from './tos';

export const SET_SOCIAL_PROVIDER = 'setSocialProvider';
export const setSocialProvider = (socialProvider) => ({
    type: SET_SOCIAL_PROVIDER,
    payload: socialProvider,
});

export const SET_SOCIAL_DATA = 'setSocialData';
export const setSocialDataAction = (socialData, socialProvider) => ({
    type: SET_SOCIAL_DATA,
    payload: { socialData, socialProvider },
});

export const SET_SOCIAL_LINKED_ACCOUNT = 'SET_SOCIAL_LINKED_ACCOUNT';
export const setSocialLinkedAccount = (socialLinkedAccountResult) => ({
    type: SET_SOCIAL_LINKED_ACCOUNT,
    payload: socialLinkedAccountResult,
});

export const RE_REQUEST_PERMISSIONS = 'setReRequestPermissionStatus';
export const setReRequestPermissionStatus = () => ({
    type: RE_REQUEST_PERMISSIONS,
    payload: true,
});

export const handleLoginSuccess = ({ state, response, dispatch }) => {
    let redirectUrl = response['redirect_url'];

    if (response['new_user'] && shouldOnboardingRedirect(state)) {
        redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
    }

    dispatch(submitSuccessRedirect(redirectUrl));
};

// TODO EB-160563 Check social-login-facebook, it should be adding the social provider there.
export const socialConnectVerified = (socialProvider, socialResponse) => (
    dispatch,
    getState,
) => {
    const {
        social: { socialSSOSettings },
    } = getState();

    dispatch(clearAuthNotification());

    // add param to call api, depending on feature flag
    let skipCreation = false;

    if (socialSSOSettings) {
        skipCreation = socialSSOSettings.twoStepsSignup || false;
    }

    dispatch(
        socialLogInUser({
            skipCreation,
            ...socialResponse,
        }),
    );
};

export const socialProviderClick = (
    socialProvider,
    stateFromProvider,
    isOneTap = false,
) => (dispatch, getState) => {
    const {
        social: { skipSocialCalls },
    } = getState();

    dispatch(setTosSocialAccepted(false));

    dispatch(
        trackSocialProviderClick({
            socialProvider,
            stateFromProvider,
        }),
    );
    if (isOneTap) {
        dispatch(trackGoogleOneTapClicked(socialProvider));
    }

    dispatch(setSocialProvider(socialProvider));

    if (skipSocialCalls) {
        dispatch(onSkipSocialLinks());
    }
};

/**
 * Tries to log in an user coming from a social provider.
 * For apple we have added an intermediate step to let the
 * user add a different account to link if we could not find
 * a matching one with the social email.
 * For facebook and google the login is handled by the api direclty.
 */
// Check this one, it should be executed after the socialLoginClick
export const socialLogInUser = (data = {}) => (dispatch, getState) => {
    const {
        social: { socialProvider },
    } = getState();

    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE) {
        return handleAppleLoginAttempt({
            data,
            getState,
            dispatch,
        });
    } else {
        dispatch(setSocialDataAction(data, socialProvider));

        lookupUser({
            email: data.email,
            sub: data.userID,
        })(dispatch, getState)
            .then((response) => {
                dispatch(handleSocialLogin({ data }));
            })
            .catch((error = '') => {
                const { message } = error;
                if (
                    message === LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT ||
                    message === LOOKUP_SOCIAL_USER_UNEXISTENT
                ) {
                    dispatch(showToS({ actionName: TOSMODAL_SOCIAL_SHOWED }));
                } else {
                    dispatch(handleSocialLogin({ data }));
                }
            });
    }
};

export const handleSocialLogin = ({ data }) => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialProvider },
    } = state;

    return (
        socialLogInApi(
            parseFormPostData(getSocialLogInFormData(data, getState)),
        )
            .then((response) => {
                if (response['is_account_autolinked'] === true) {
                    trackAccountAutolinked({
                        state,
                        sourceProvider: socialProvider,
                    });
                    trackLoginSuccessful({
                        sourceProvider: socialProvider,
                        state,
                    });
                } else {
                    if (
                        socialProvider ===
                        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK
                    ) {
                        trackFBLoginSuccess(state, response);
                    } else if (
                        socialProvider ===
                        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE
                    ) {
                        trackGoogleLoginSuccess(state, response);
                    }
                }
                handleLoginSuccess({ state, response, dispatch });
            })
            // User may be found in the lookup call or have failed
            .catch(handleOnError(dispatch, state, data))
    );
};

// Used in checkout FB Login
export const socialLogInCheckoutUser = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();

    return (
        socialLogInApi(
            parseFormPostData(getSocialLogInFormData(data, getState)),
        )
            // Send tracking data first so we don't lose it when we close the
            // iframe inside of checkoutWidget
            .then((response) => {
                return response;
            })
            .then((response) => {
                trackFBLoginSuccess(state, response, GA_AUTH_CHECKOUT_CATEGORY);
                notifyParent('checkoutLoginSuccessful');
            })
            .catch(
                handleOnError(dispatch, state, data, GA_AUTH_CHECKOUT_CATEGORY),
            )
    );
};

/* Needed for SIWA, returning to /signin when the email belongs to a soft
 * account there is not a real api call that fails, but we need to trigger
 * the same `onError` that Facebook returns in the same scenario.
 */
export const socialLogInSoftUser = () => (dispatch, getState) => {
    const state = getState();

    handleOnError(
        dispatch,
        state,
    )({ name: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR });
};

export const socialLogFacebookUser = () => (dispatch, getState) => {
    dispatch(
        showAuthNotification({
            message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
            iconType: <AlertChunky />,
        }),
    );
};

// TODO EB-160563 Check social-login-facebook, it should be adding the social provider there.
export const socialLogInUserNotAuthorized = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        social: { socialProvider },
    } = state;

    if (socialProvider) {
        const error = get(
            data,
            'status.error',
            get(data, 'status', 'not_defined'),
        );
        dispatch(handleSocialNotAuthorizedError(error));
        trackSocialPermissionNotGranted({
            state,
            errorCode: error,
            socialProvider,
        });
    }
};

// TODO EB-160563 Check social-login-facebook, it should be adding the social provider there.
export const socialLogInUserAuthorized = (socialProvider, socialData) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        social: { socialProvider },
    } = state;

    if (
        includes(
            [
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
            ],
            socialProvider,
        )
    ) {
        trackSocialPermissionGranted({ state, socialProvider });
    }
};

export const socialSignUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return (
        socialLogInApi(parseFormPostData(getSocialSignUpFormData(data, state)))
            .then((response) => {
                trackFBLoginSuccess(state, response);
                let redirectUrl = response['redirect_url'];

                if (shouldOnboardingRedirect(state)) {
                    redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
                }
                return dispatch(submitSuccessRedirect(redirectUrl));
            })
            .then(() => trackSocialSignupEvent({ state }))
            // pass returned data so it can be read and used to make decisions
            .catch(handleOnError(dispatch, state, data))
    );
};

/**
 * Calling social_action but handling an empty response for it because
 * we only want to store the data on redis to allow other actions like
 * * Forgot password
 * @param {string} email
 * @param {string} accessToken
 */
export const storeSocialDataOnRedis = ({ email, accessToken }) => (
    dispatch,
    getState,
) => {
    const {
        social: { socialProvider: sourceProvider },
    } = getState();

    return socialLogInApi(
        parseFormPostData({ email, accessToken, sourceProvider }),
    )
        .then(() => {
            Promise.resolve();
        })
        .catch(() => {
            Promise.resolve();
        });
};
/**
 * Catch the error and shows the specific error message to the user.
 */
export const handleSocialNotAuthorizedError = (error) => (
    dispatch,
    getState,
) => {
    switch (error) {
        case 'popup_blocked_by_browser':
            dispatch(
                showAuthNotification({
                    message: SOCIAL_ERROR_POPUP_BLOCKED,
                }),
            );
            break;

        case 'unknown': // Most common error in FB
        case 'not_defined': // No error code
            dispatch(
                showAuthNotification({
                    message: SOCIAL_ERROR_UNKNOWN,
                }),
            );
            break;

        case 'access_denied':
        case 'not_authorized': // FB Error when user not authorize
        case 'user_cancelled_authorize': // On Apple, user cancel after login
            dispatch(
                showAuthNotification({
                    message: SOCIAL_ERROR_NOT_AUTHORIZED,
                }),
            );
            break;

        case 'network_error': //temporal error
        case 'server_error':
            dispatch(
                showAuthNotification({
                    message: SOCIAL_ERROR_SERVER,
                }),
            );
            break;
    }
};

/**
 * Load Google OneTap component
 */
export const loadGoogleOneTap = () => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialSSOSettings },
    } = state;
    const { clientId } = socialSSOSettings[GA_GOOGLE];
    googleLoginOneTap({
        clientId,
        onReturn: socialProviderClick,
        onVerified: socialConnectVerified,
        onTriggered: trackGoogleOneTapTriggered,
        onNoDisplayed: trackGoogleOneTapNotDisplayed,
        onBlocked: trackGoogleOneTapBlocked,
        dispatch,
    });
};
